// /Users/pramod/Desktop/school-erp/frontend/src/Parent/ChildDetails.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api';
import Header from './components/Header';
import SubjectList from './components/SubjectList';
import AttendanceView from './components/AttendanceView';
import CalendarView from './components/CalendarView';
import ParentMarksView from './components/ParentMarksView';

const ChildDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null); // 'subjects' | 'attendance' | 'marks' | 'calendar'
  const [childData, setChildData] = useState(null);
  const [academicContext, setAcademicContext] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  
  // Calendar state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarGrouped, setCalendarGrouped] = useState({});
  const [eventTypes, setEventTypes] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  
  // Today/Upcoming events
  const [todayEvents, setTodayEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  
  // Marks state
  const [marksData, setMarksData] = useState(null);
  const [marksLoading, setMarksLoading] = useState(false);
  const [marksError, setMarksError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState({
    academicYearId: null,
    programTypeId: null,
    programId: null,
    termId: null,
    semesterId: null
  });
  const [showMarksModal, setShowMarksModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);

  const initialFetchDone = useRef(false);

  const studentId = location.state?.studentId;
  const childFromState = location.state?.childData;

  // Fetch marks data function
  const fetchMarksData = useCallback(async (periodOverride = null) => {
    if (!studentId) return;

    try {
      setMarksLoading(true);
      setMarksError(null);

      const period = periodOverride || selectedPeriod;
      const { academicYearId, programTypeId, programId, termId, semesterId } = period;

      if (!academicYearId || !programTypeId || !programId) {
        console.warn('Missing required parameters for marks fetch');
        setMarksLoading(false);
        return;
      }

      const params = new URLSearchParams({
        academicYearId,
        programTypeId,
        programId
      });

      if (termId) {
        params.append('termId', termId);
      } else if (semesterId) {
        params.append('semesterId', semesterId);
      }

      const response = await api.get(`/api/parent/child/${studentId}/marks?${params.toString()}`);
      
      if (response.data.success) {
        setMarksData(response.data.data);
        console.log('✅ Parent marks fetched successfully');
      }
    } catch (err) {
      console.error('❌ Error fetching parent marks:', err);
      setMarksError(err.response?.data?.message || 'Failed to load marks');
      toast.error('Failed to load marks data');
    } finally {
      setMarksLoading(false);
    }
  }, [studentId, selectedPeriod]);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    if (!studentId) return;
    
    try {
      setLoading(true);
      setError(null);

      // Fetch academic context
      const contextRes = await api.get(`/api/parent/child/${studentId}/academic-context`);
      if (contextRes.data.success) {
        const context = contextRes.data.data;
        setAcademicContext(context);
        
        if (context.hierarchy) {
          const h = context.hierarchy;
          setSelectedPeriod({
            academicYearId: h.academicYear?.id || null,
            programTypeId: h.programType?.id || null,
            programId: h.program?.id || null,
            termId: h.term?.id || null,
            semesterId: h.semester?.number ? h.semester.number : null
          });
        }
      }

      // Fetch attendance
      const attendanceRes = await api.get(`/api/parent/child/${studentId}/attendance`);
      if (attendanceRes.data.success) {
        setAttendanceData(attendanceRes.data.data);
      }

      // Fetch calendar events
      const calendarRes = await api.get(`/api/parent/academic-calendar?studentId=${studentId}`);
      if (calendarRes.data.success) {
        const data = calendarRes.data.data;
        setCalendarEvents(data.events || []);
        setCalendarGrouped(data.groupedByMonth || {});
        setEventTypes(data.eventTypes || []);
        setTotalEvents(data.total || 0);
      }

      // Fetch today's events
      const eventsRes = await api.get(`/api/parent/today-events?studentId=${studentId}`);
      if (eventsRes.data.success) {
        const data = eventsRes.data.data;
        setTodayEvents(data.todayEvents || []);
        setUpcomingEvents(data.upcomingEvents || []);
      }

      // Fetch notifications
      const notifRes = await api.get('/api/parent/notifications');
      if (notifRes.data.success) {
        setNotifications(notifRes.data.data || []);
      }

      if (childFromState) {
        setChildData(childFromState);
      }
    } catch (err) {
      console.error('Error fetching child data:', err);
      setError(err.response?.data?.message || 'Unable to load child details. Please try again.');
      toast.error('Failed to load child details');
    } finally {
      setLoading(false);
    }
  }, [studentId, childFromState]);

  // Effect to fetch marks after period is set
  useEffect(() => {
    const { academicYearId, programTypeId, programId } = selectedPeriod;
    if (academicYearId && programTypeId && programId && !loading) {
      fetchMarksData();
    }
  }, [selectedPeriod, fetchMarksData, loading]);

  // Initial data fetch
  useEffect(() => {
    if (studentId && !initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchAllData();
    }
  }, [studentId, fetchAllData]);

  const handleBack = () => navigate('/parent/dashboard');
  
  const handleRefresh = () => {
    initialFetchDone.current = false;
    setMarksData(null);
    fetchAllData();
    toast.info('Updating information...');
  };

  // Handle subject click to show marks modal
  const handleSubjectClick = (subject) => {
    setSelectedSubject(subject);
    setShowMarksModal(true);
  };

  // Handle card click - opens modal with content
  const handleCardClick = (cardType) => {
    setSelectedCard(cardType);
  };

  // Close modal
  const closeModal = () => {
    setSelectedCard(null);
  };

  const getInitials = (name) => {
    if (!name) return 'S';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getAcademicHierarchy = () => {
    if (!academicContext) return null;
    
    const hierarchy = academicContext.hierarchy || {};
    const parts = [];
    
    if (hierarchy.programType?.name) {
      parts.push({ label: 'Level', value: hierarchy.programType.name });
    }
    if (hierarchy.program?.name) {
      parts.push({ label: 'Course/Stream', value: hierarchy.program.name });
    }
    if (hierarchy.academicYear?.name) {
      parts.push({ label: 'School Year', value: hierarchy.academicYear.name });
    }
    if (hierarchy.semester) {
      parts.push({ label: 'Term/Semester', value: hierarchy.semester.name });
    } else if (hierarchy.term) {
      parts.push({ label: 'Term/Semester', value: hierarchy.term.label });
    }
    if (hierarchy.class?.name) {
      parts.push({ label: 'Class/Section', value: hierarchy.class.name });
    }
    
    return parts;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header parentData={{ parentName: 'Parent' }} notifications={[]} />
        <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8 space-y-6">
          <div className="h-6 w-32 bg-slate-200 rounded-lg animate-pulse"></div>
          <div className="bg-white rounded-2xl p-6 border border-slate-100 space-y-4 shadow-sm">
            <div className="flex items-center gap-4 animate-pulse">
              <div className="w-16 h-16 bg-slate-200 rounded-2xl"></div>
              <div className="flex-1 space-y-2">
                <div className="h-6 w-48 bg-slate-200 rounded-lg"></div>
                <div className="h-4 w-32 bg-slate-200 rounded-lg"></div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl h-32 border border-slate-100 shadow-sm animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header parentData={{ parentName: 'Parent' }} notifications={[]} />
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-xl p-8">
            <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-rose-100">
              <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-950 mb-2">Something went wrong</h3>
            <p className="text-slate-500 text-sm mb-6 leading-relaxed">{error}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleBack}
                className="px-4 py-2.5 bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl border border-slate-200 hover:bg-slate-100 active:scale-95 transition-all"
              >
                Go Back
              </button>
              <button
                onClick={handleRefresh}
                className="px-4 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl shadow-md shadow-indigo-100 hover:bg-indigo-700 active:scale-95 transition-all"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const childName = childData?.name || academicContext?.studentName || 'Student';
  const usn = academicContext?.usn || childData?.usn || 'N/A';
  const academicHierarchy = getAcademicHierarchy();
  const studentStatus = childData?.status || 'active';
  const isActive = studentStatus === 'active';

  // Card data
  const cards = [
    {
      id: 'subjects',
      icon: '📚',
      title: 'Subjects',
      description: `${academicContext?.subjects?.core?.length || 0} Core • ${academicContext?.subjects?.elective?.length || 0} Elective`,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      count: (academicContext?.subjects?.core?.length || 0) + (academicContext?.subjects?.elective?.length || 0)
    },
    {
      id: 'attendance',
      icon: '📊',
      title: 'Attendance',
      description: `Overall: ${attendanceData?.summary?.percentage || 0}%`,
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      count: `${attendanceData?.summary?.present || 0}/${attendanceData?.summary?.total || 0}`
    },
    {
      id: 'marks',
      icon: '📝',
      title: 'Marks',
      description: `${marksData?.subjects?.length || 0} Subjects • CIE Scores`,
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      count: `${marksData?.subjects?.filter(s => s.marks?.length > 0).length || 0} Graded`
    },
    {
      id: 'calendar',
      icon: '📅',
      title: 'Calendar',
      description: `${totalEvents} Events • ${todayEvents.length} Today`,
      color: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      count: `${upcomingEvents.length} Upcoming`
    }
  ];

  // Render content based on selected card
  const renderModalContent = () => {
    switch (selectedCard) {
      case 'subjects':
        return (
          <SubjectList
            subjects={academicContext?.subjects?.core || []}
            electiveSubjects={academicContext?.subjects?.elective || []}
            title="Enrolled Subjects"
            onSubjectClick={(subject) => {
              closeModal();
              handleSubjectClick(subject);
            }}
          />
        );
      case 'attendance':
        return <AttendanceView data={attendanceData} />;
      case 'marks':
        return (
          <ParentMarksView
            studentId={studentId}
            academicContext={academicContext}
            marksData={marksData}
            marksLoading={marksLoading}
            marksError={marksError}
            selectedPeriod={selectedPeriod}
            setSelectedPeriod={setSelectedPeriod}
            fetchMarksData={fetchMarksData}
            onSubjectClick={(subject) => {
              closeModal();
              handleSubjectClick(subject);
            }}
          />
        );
      case 'calendar':
        return (
          <CalendarView
            events={calendarEvents}
            groupedEvents={calendarGrouped}
            eventTypes={eventTypes}
            totalEvents={totalEvents}
            todayEvents={todayEvents}
            upcomingEvents={upcomingEvents}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 antialiased selection:bg-indigo-100 pb-8">
      <Header parentData={{ parentName: 'Parent' }} notifications={notifications} />

      <main className="max-w-4xl mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        
        {/* Back & Refresh Bar */}
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={handleBack}
            className="group flex items-center gap-1.5 sm:gap-2 text-slate-500 hover:text-slate-900 text-xs sm:text-sm font-medium transition-colors"
          >
            <div className="p-1.5 rounded-lg bg-white border border-slate-200 group-hover:border-slate-300 shadow-sm transition-all">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 transform group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </button>

          <button
            onClick={handleRefresh}
            className="flex items-center gap-1 px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-lg bg-white border border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-900 text-[11px] sm:text-xs font-semibold shadow-sm active:scale-95 transition-all"
          >
            <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.253 8H18" />
            </svg>
            <span className="hidden sm:inline">Refresh</span>
            <span className="sm:hidden">↻</span>
          </button>
        </div>

        {/* Student Profile Card */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-6 transition-all duration-300">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            
            {/* Avatar & Name */}
            <div className="flex items-center gap-3 sm:gap-4">
              <div className={`w-14 h-14 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-white text-lg sm:text-2xl font-bold shadow-md shadow-slate-100 border transition-all flex-shrink-0 ${
                isActive ? 'bg-gradient-to-tr from-indigo-600 to-violet-500 border-indigo-200' : 'bg-slate-400 border-slate-300'
              }`}>
                {getInitials(childName)}
              </div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-black tracking-tight text-slate-900 truncate">
                  {childName}
                </h1>
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-0.5">
                  <span className="inline-flex items-center text-[10px] sm:text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200/60 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg">
                    Roll: {usn}
                  </span>
                  <span className={`inline-flex items-center text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg border ${
                    isActive 
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                      : 'bg-rose-50 text-rose-700 border-rose-200'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full mr-1 ${isActive ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Academic Hierarchy */}
            {academicHierarchy && academicHierarchy.length > 0 && (
              <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5 sm:gap-2 bg-slate-50/80 rounded-xl p-2.5 sm:p-4 border border-slate-100/70">
                {academicHierarchy.slice(0, 4).map((item, index) => (
                  <div key={index} className="flex flex-col">
                    <span className="text-[9px] sm:text-[10px] text-slate-400 font-medium uppercase tracking-wider">
                      {item.label}
                    </span>
                    <span className="text-[10px] sm:text-xs font-semibold text-slate-700 truncate">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>

        {/* Card Grid - 2 columns on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {cards.map((card) => (
            <button
              key={card.id}
              onClick={() => handleCardClick(card.id)}
              className={`group relative bg-white rounded-2xl border ${card.borderColor} shadow-sm p-4 sm:p-5 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 active:scale-95 text-left overflow-hidden`}
            >
              {/* Gradient overlay on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
              
              {/* Icon */}
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${card.iconBg} flex items-center justify-center text-xl sm:text-2xl mb-2 sm:mb-3`}>
                {card.icon}
              </div>
              
              {/* Title */}
              <h3 className="text-sm sm:text-base font-bold text-slate-900">
                {card.title}
              </h3>
              
              {/* Description */}
              <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 line-clamp-1">
                {card.description}
              </p>
              
              {/* Count badge */}
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
                <span className={`inline-flex items-center justify-center text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full ${card.iconBg} ${card.iconColor} border ${card.borderColor}`}>
                  {card.count}
                </span>
              </div>
              
              {/* Arrow indicator */}
              <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          ))}
        </div>

        {/* Quick Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <div className="bg-white rounded-xl border border-slate-100 px-3 py-2.5 sm:px-4 sm:py-3 text-center shadow-sm">
            <p className="text-[10px] sm:text-xs text-slate-400 font-medium uppercase tracking-wider">Subjects</p>
            <p className="text-sm sm:text-base font-bold text-slate-900">{cards[0].count}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 px-3 py-2.5 sm:px-4 sm:py-3 text-center shadow-sm">
            <p className="text-[10px] sm:text-xs text-slate-400 font-medium uppercase tracking-wider">Attendance</p>
            <p className="text-sm sm:text-base font-bold text-emerald-600">{attendanceData?.summary?.percentage || 0}%</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 px-3 py-2.5 sm:px-4 sm:py-3 text-center shadow-sm">
            <p className="text-[10px] sm:text-xs text-slate-400 font-medium uppercase tracking-wider">Graded</p>
            <p className="text-sm sm:text-base font-bold text-indigo-600">{cards[2].count}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 px-3 py-2.5 sm:px-4 sm:py-3 text-center shadow-sm">
            <p className="text-[10px] sm:text-xs text-slate-400 font-medium uppercase tracking-wider">Events</p>
            <p className="text-sm sm:text-base font-bold text-amber-600">{totalEvents}</p>
          </div>
        </div>
      </main>

      {/* Detail Modal - Full Screen on Mobile, Centered on Desktop */}
      {selectedCard && (
        <div 
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center overflow-hidden"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
            onClick={closeModal}
          />

          {/* Modal Content */}
          <div 
            className="relative bg-white w-full rounded-t-3xl sm:rounded-2xl shadow-2xl max-w-4xl max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden border border-slate-200/40 animate-slide-up sm:animate-scale-in"
          >
            {/* Drag Handle - Mobile Only */}
            <div className="w-12 h-1 bg-slate-300 rounded-full mx-auto my-3 sm:hidden shrink-0" />

            {/* Modal Header */}
            <div className="bg-white border-b border-slate-100 px-4 sm:px-6 py-3.5 sm:py-4 shrink-0 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${cards.find(c => c.id === selectedCard)?.iconBg} flex items-center justify-center text-lg sm:text-xl`}>
                  {cards.find(c => c.id === selectedCard)?.icon}
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-slate-900">
                    {cards.find(c => c.id === selectedCard)?.title}
                  </h2>
                  <p className="text-[10px] sm:text-xs text-slate-500">
                    {cards.find(c => c.id === selectedCard)?.description}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 bg-slate-50 hover:bg-slate-100 transition-all p-2 rounded-xl"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50/50">
              {renderModalContent()}
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 border-t border-slate-200/80 px-4 sm:px-6 py-3 sm:py-4 shrink-0 flex items-center justify-between text-[10px] sm:text-xs text-slate-400 font-medium">
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {childName} • {usn}
              </span>
              <span>
                {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Subject Detail Modal - Nested Modal for Subject Details */}
      {showMarksModal && selectedSubject && (
        <div 
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center overflow-hidden"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowMarksModal(false);
          }}
        >
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm transition-opacity" 
            onClick={() => setShowMarksModal(false)}
          />

          {/* Modal Content */}
          <div className="relative bg-white w-full rounded-t-3xl sm:rounded-2xl shadow-2xl max-w-3xl max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden border border-slate-200/40 animate-slide-up sm:animate-scale-in">
            
            {/* Drag Handle - Mobile Only */}
            <div className="w-12 h-1 bg-slate-300 rounded-full mx-auto my-3 sm:hidden shrink-0" />

            {/* Header - Gradient */}
            <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-violet-600 px-4 sm:px-6 py-4 sm:py-5 shrink-0">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/20 text-white text-[9px] sm:text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm">
                      📝 Subject Details
                    </span>
                    {selectedSubject.subjectCode && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-white/10 text-white/80 text-[9px] sm:text-[10px] font-mono">
                        {selectedSubject.subjectCode}
                      </span>
                    )}
                  </div>
                  <h2 className="text-lg sm:text-2xl font-bold text-white tracking-tight leading-tight truncate">
                    {selectedSubject.subjectName}
                  </h2>
                  <p className="text-xs sm:text-sm text-indigo-200/90 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{childName}</span>
                    <span className="hidden xs:inline text-indigo-300/50">•</span>
                    <span className="text-indigo-300/80 font-mono text-[10px] sm:text-xs">{usn}</span>
                  </p>
                </div>

                <button
                  onClick={() => setShowMarksModal(false)}
                  className="text-white/70 hover:text-white bg-white/10 hover:bg-white/20 transition-all p-2 rounded-xl shrink-0"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50/50">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {/* CIE Score Summary */}
                <div className="bg-slate-50/80 border-b border-slate-200 px-4 py-3 sm:px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <span className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                    <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    CIE Cumulative Total
                  </span>
                  <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-xl bg-indigo-50 border border-indigo-100">
                    <span className="text-sm sm:text-base font-bold text-indigo-700 font-mono">
                      {selectedSubject.cieScore?.obtained || 0}
                    </span>
                    <span className="text-indigo-300 font-medium">/</span>
                    <span className="text-sm sm:text-base font-medium text-slate-500 font-mono">
                      {selectedSubject.cieScore?.max || 0}
                    </span>
                  </div>
                </div>

                {/* Marks List */}
                {selectedSubject.marks && selectedSubject.marks.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {selectedSubject.marks.map((mark, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-3.5 hover:bg-slate-50/50 transition-colors"
                      >
                        <span className="text-sm sm:text-base font-medium text-slate-700 flex items-center gap-2">
                          <span className="text-xs text-slate-400 font-semibold w-5 sm:w-6">
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                          {mark.label}
                          {mark.label?.toLowerCase().includes('attendance') && (
                            <span className="text-[8px] sm:text-[9px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded uppercase tracking-wider">
                              Auto
                            </span>
                          )}
                        </span>
                        <div className="flex items-center gap-2 font-mono">
                          <span className="text-base sm:text-lg font-bold text-slate-900">
                            {mark.obtained}
                          </span>
                          <span className="text-slate-300 font-medium">/</span>
                          <span className="text-sm sm:text-base font-medium text-slate-400">
                            {mark.max}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 px-6 text-center">
                    <div className="text-4xl mb-3">📭</div>
                    <p className="text-sm font-medium text-slate-500">No marks recorded</p>
                    <p className="text-xs text-slate-400 mt-1">Assessment data will appear here once available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 border-t border-slate-200/80 px-4 sm:px-6 py-3 sm:py-4 shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[10px] sm:text-xs text-slate-400 font-medium">
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {selectedSubject.marks?.length || 0} Components
              </span>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                  <span>Obtained</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-slate-300"></span>
                  <span>Maximum</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS Styles */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes scaleIn {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
        
        .animate-scale-in {
          animation: scaleIn 0.25s ease-out;
        }
        
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        @media (min-width: 480px) {
          .xs\\:inline {
            display: inline;
          }
        }
        
        * {
          -webkit-tap-highlight-color: transparent;
        }
      `}</style>
    </div>
  );
};

export default ChildDetails;